import { createBrowserHistory } from 'history'

const history = createBrowserHistory()

class ContentEngine {
    program = null
    subIndex = 0
    screenIndex = 0

    currentScreenData = null

    listeners = []

    /** 'left' when going forward (next), 'right' when going backward (prev) */
    slideDirection = null

    /**
     * When true, all content is fetched from the admin STAGING endpoints
     * (unpublished work) instead of the live ones. Set by the runner before
     * loadProgram() so admins can preview drafts without publishing to live.
     */
    staging = false

    /**
     * Callback fired when the last screen of the current subprogram is
     * completed (i.e. next() is called on the final screen).
     * Set by SubprogramRunner before loading.
     */
    onSubprogramComplete = null

    BACKEND_URL = process.env.REACT_APP_API_BASE

    async loadProgram(programId) {
        const url = this.staging
            ? `${this.BACKEND_URL}/api/program/staging-preview/${programId}`
            : `${this.BACKEND_URL}/api/program/${programId}`
        const res = await fetch(
            url,
            this.staging ? { credentials: 'include' } : undefined
        )
        this.program = await res.json()

        this.loadProgress()

        // validate indices
        if (this.subIndex >= this.program.subprograms.length) {
            this.subIndex = 0
            this.screenIndex = 0
        }

        const currentSub = this.program.subprograms[this.subIndex]

        if (!currentSub || this.screenIndex >= currentSub.screens.length) {
            this.screenIndex = 0
        }

        await this.loadCurrentScreenData()

        this.notify()
    }

    getCurrentScreen() {
        if (!this.program) return null
        const sub = this.program.subprograms[this.subIndex]

        if (!sub) return null
        const screen = sub.screens[this.screenIndex]

        if (!screen) return null
        return screen
    }

    getCurrentSubprogram() {
        if (!this.program) return null
        return this.program.subprograms[this.subIndex]
    }

    // ── Screen visibility ────────────────────────────────────────────────
    // Screens flagged `hidden` in the admin are skipped for the end user
    // (navigation + timeline) but stay in the array so indices remain stable
    // and other screens can still reference them by id (e.g. Play fetching an
    // Assembly screen). Screens without the field default to visible.
    isScreenVisible(screen) {
        return !!screen && !screen.hidden
    }

    // First visible screen index scanning from `startIdx` in direction `dir`
    // (+1 forward, -1 backward). Returns -1 when none found.
    _firstVisibleFrom(startIdx, dir = 1) {
        const sub = this.getCurrentSubprogram()
        if (!sub || !Array.isArray(sub.screens)) return -1
        for (
            let i = startIdx;
            i >= 0 && i < sub.screens.length;
            i += dir
        ) {
            if (this.isScreenVisible(sub.screens[i])) return i
        }
        return -1
    }

    // If the current index landed on a hidden screen (initial load, restored
    // progress, or a direct goTo), nudge it to the nearest visible screen.
    _ensureVisibleScreen() {
        const sub = this.getCurrentSubprogram()
        if (!sub || !Array.isArray(sub.screens) || sub.screens.length === 0)
            return
        if (this.isScreenVisible(sub.screens[this.screenIndex])) return
        let idx = this._firstVisibleFrom(this.screenIndex, 1)
        if (idx === -1) idx = this._firstVisibleFrom(this.screenIndex, -1)
        if (idx !== -1) this.screenIndex = idx
    }

    async loadCurrentScreenData() {
        this._ensureVisibleScreen()
        const screen = this.getCurrentScreen()
        if (!screen) return

        const projectId = screen.projectId || this.getCurrentSubprogram()?.id

        // Build URL: include screenId when available (new multi-screen format).
        // In staging mode read the unpublished screen from the admin endpoint.
        const apiBase = this.staging
            ? `${this.BACKEND_URL}/api/staging/${screen.type}/${projectId}`
            : `${this.BACKEND_URL}/api/${screen.type}/${projectId}`
        let url = apiBase
        if (screen.id) {
            url += `/${screen.id}`
        }

        try {
            const res = await fetch(
                url,
                this.staging ? { credentials: 'include' } : undefined
            )
            if (!res.ok) throw new Error('Failed to fetch screen')
            this.currentScreenData = await res.json()
        } catch (err) {
            console.error('Screen load error:', err)
            this.currentScreenData = null
        }
    }

    async next() {
        if (!this.program) return
        const currentSub = this.program.subprograms[this.subIndex]
        // Advance to the next VISIBLE screen, skipping any hidden ones.
        const nextIdx = this._firstVisibleFrom(this.screenIndex + 1, 1)
        if (nextIdx !== -1) {
            // Move to next screen within the SAME subprogram
            this.screenIndex = nextIdx
            this.slideDirection = 'left'

            // --- FIX: Force the next screen to start at step 1 ---
            this._pendingStep = 1
        } else {
            // Last screen of this subprogram — fire completion callback
            console.log('Subprogram complete:', currentSub.id)
            if (this.onSubprogramComplete) {
                this.onSubprogramComplete()
                return
            }
            // No callback set — just log
            console.log('No onSubprogramComplete callback set')
            return
        }

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    async prev() {
        if (!this.program) return
        // Step back to the previous VISIBLE screen, skipping any hidden ones.
        const prevIdx = this._firstVisibleFrom(this.screenIndex - 1, -1)
        if (prevIdx !== -1) {
            // 1. Find out how many steps the target (previous) screen has
            const sub = this.getCurrentSubprogram()
            const prevScreen = sub?.screens?.[prevIdx]
            const prevScreenTotalSteps = prevScreen?.stepCount || 1

            // 2. Move the screen index back
            this.screenIndex = prevIdx
            this.slideDirection = 'right'

            // 3. Set the pending step to the LAST step of that screen
            this._pendingStep = prevScreenTotalSteps
        } else {
            // Already on the first screen of this subprogram — go back
            history.back()
            return
        }

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    async goTo(subIdx, screenIdx) {
        if (!this.program) return
        if (subIdx < 0 || subIdx >= this.program.subprograms.length) return
        const sub = this.program.subprograms[subIdx]
        if (screenIdx < 0 || screenIdx >= sub.screens.length) return

        this.subIndex = subIdx
        this.screenIndex = screenIdx

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    subscribe(fn) {
        this.listeners.push(fn)

        return () => {
            this.listeners = this.listeners.filter((l) => l !== fn)
        }
    }

    notify() {
        this.listeners.forEach((fn) => fn())
    }

    saveProgress() {
        const progress = {
            subIndex: this.subIndex,
            screenIndex: this.screenIndex,
        }

        sessionStorage.setItem('courseProgress', JSON.stringify(progress))
    }

    getProgress() {
        return `${this.subIndex}_${this.screenIndex}`
    }

    loadProgress() {
        const saved = sessionStorage.getItem('courseProgress')

        if (!saved) {
            this.subIndex = 0
            this.screenIndex = 0
            return
        }

        try {
            const progress = JSON.parse(saved)

            this.subIndex = progress.subIndex ?? 0
            this.screenIndex = progress.screenIndex ?? 0
        } catch {
            this.subIndex = 0
            this.screenIndex = 0
        }
    }

    // ── Step count tracking for continuous numbering across screens ──

    /**
     * Register the total step count for a screen.
     * Now a no-op — step counts come from the backend payload.
     * Kept for backward-compatibility so existing screen components
     * don't throw if they still call it during migration.
     */
    registerScreenStepCount(_subIdx, _screenIdx, _count) {
        // no-op: step counts are provided by the backend in program.subprograms[].screens[].stepCount
    }

    /**
     * Get the cumulative step offset for the current screen.
     * Returns the sum of stepCount from all previous screens
     * in the same subprogram, using backend-provided data.
     */
    getStepOffset() {
        const sub = this.getCurrentSubprogram()
        if (!sub) return 0
        let offset = 0
        for (let i = 0; i < this.screenIndex; i++) {
            // Hidden screens are invisible to the learner, so their steps must
            // not count toward the visible global step numbering.
            if (sub.screens[i]?.hidden) continue
            offset += sub.screens[i]?.stepCount || 0
        }
        return offset
    }

    /**
     * Get step info for ALL screens in the current subprogram.
     * Returns array of { screenIndex, screenType, stepCount, offset }.
     * Uses backend-provided stepCount from program payload.
     */
    getAllScreenSteps() {
        const sub = this.getCurrentSubprogram()
        if (!sub) return []
        const result = []
        let cumOffset = 0
        for (let i = 0; i < sub.screens.length; i++) {
            // Omit hidden screens from the timeline entirely. The real array
            // index is preserved on each entry so goToScreen still targets the
            // correct screen.
            if (sub.screens[i]?.hidden) continue
            const count = sub.screens[i]?.stepCount || 0
            result.push({
                screenIndex: i,
                screenType: sub.screens[i].type,
                stepCount: count,
                offset: cumOffset,
            })
            cumOffset += count
        }
        return result
    }

    /**
     * Navigate to a specific screen and step within the current subprogram.
     * stepInScreen is 1-based local step number.
     */
    async goToScreen(screenIdx, stepInScreen) {
        if (!this.program) return
        const sub = this.getCurrentSubprogram()
        if (!sub || screenIdx < 0 || screenIdx >= sub.screens.length) return

        if (screenIdx !== this.screenIndex) {
            this.slideDirection =
                screenIdx > this.screenIndex ? 'left' : 'right'
            this.screenIndex = screenIdx
            this.saveProgress()
            await this.loadCurrentScreenData()
        }

        // Store the target step so the screen component can pick it up
        this._pendingStep = stepInScreen
        this.notify()
    }
}

export default new ContentEngine()
